import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import BaseModal from '@/components/modals/BaseModal';
import {
  assignUserToFirmware,
  removeUserFromFirmware,
  formatPrototypeFirmwareError,
} from '@/repository/PrototypeFirmwareDAO';
import {
  useToast,
  ToastType,
} from '@/context_providers/toast_context/ToastContext';
import type { PrototypeFirmware } from '@/models/PrototypeFirmware/PrototypeFirmware';
import type { PrototyperUserInfo } from '@/models/PrototypeFirmware/PrototyperUserInfo';

type Props = {
  firmware: PrototypeFirmware | null;
  prototypers: PrototyperUserInfo[];
  onClose: () => void;
  refetchFirmwares: () => Promise<void>;
  refetchPrototypers: () => Promise<void>;
};

const AssignUserModal: React.FC<Props> = ({
  firmware,
  prototypers,
  onClose,
  refetchFirmwares,
  refetchPrototypers,
}) => {
  const { showToast } = useToast();
  const [busyAuthId, setBusyAuthId] = useState<string | null>(null);
  const [autocompleteValue, setAutocompleteValue] =
    useState<PrototyperUserInfo | null>(null);

  const assignedAuthIds = useMemo(
    () => new Set(firmware?.assigned_auth_ids ?? []),
    [firmware],
  );

  const assignedEntries = useMemo(() => {
    if (!firmware) return [];
    return firmware.assigned_auth_ids.map((auth_id) => {
      const p = prototypers.find((u) => u.auth_id === auth_id);
      return { auth_id, user: p ?? null };
    });
  }, [firmware, prototypers]);

  const pickerOptions = useMemo(
    () => prototypers.filter((p) => !assignedAuthIds.has(p.auth_id)),
    [prototypers, assignedAuthIds],
  );

  const handleAdd = async (user: PrototyperUserInfo) => {
    if (!firmware || busyAuthId) return;
    setBusyAuthId(user.auth_id);
    try {
      await assignUserToFirmware(firmware.id, user.auth_id);
    } catch (error) {
      const { message, shouldRefetch } = formatPrototypeFirmwareError(
        error,
        'User',
      );
      showToast(message, ToastType.DANGER);
      if (shouldRefetch) {
        try {
          await Promise.all([refetchFirmwares(), refetchPrototypers()]);
        } catch {
          // parent's refetch toast covers this
        }
      }
      setBusyAuthId(null);
      return;
    }
    showToast(
      `Assigned ${user.first_name} ${user.last_name} to ${firmware.name}.`,
      ToastType.SUCCESS,
    );
    try {
      await Promise.all([refetchFirmwares(), refetchPrototypers()]);
    } catch {
      // refetch failures are surfaced by the parent's own toast.
    }
    setAutocompleteValue(null);
    setBusyAuthId(null);
  };

  const handleRemove = async (auth_id: string) => {
    if (!firmware || busyAuthId) return;
    setBusyAuthId(auth_id);
    try {
      await removeUserFromFirmware(firmware.id, auth_id);
    } catch (error) {
      const { message, shouldRefetch } = formatPrototypeFirmwareError(
        error,
        'User',
      );
      showToast(message, ToastType.DANGER);
      if (shouldRefetch) {
        try {
          await Promise.all([refetchFirmwares(), refetchPrototypers()]);
        } catch {
          // parent's refetch toast covers this
        }
      }
      setBusyAuthId(null);
      return;
    }
    showToast('Removed assignment.', ToastType.SUCCESS);
    try {
      await Promise.all([refetchFirmwares(), refetchPrototypers()]);
    } catch {
      // refetch failures are surfaced by the parent's own toast.
    }
    setBusyAuthId(null);
  };

  return (
    <BaseModal
      open={!!firmware}
      onClose={busyAuthId ? () => {} : onClose}
      title={firmware ? `Manage Users — ${firmware.name} v${firmware.version}` : ''}
      submitButtonText=""
      onSubmit={() => {}}
      footerContent={null}
      showFooter={false}
      enableEnterKey={false}
      showCloseButton={!busyAuthId}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          width: { xs: '100%', sm: 520 },
          maxWidth: '100%',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Assigned Users
          </Typography>
          {assignedEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No users assigned yet.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ width: '100%' }}>
              {assignedEntries.map(({ auth_id, user }) => (
                <ListItem
                  key={auth_id}
                  sx={{ pr: 7 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="Remove"
                      disabled={busyAuthId === auth_id}
                      onClick={() => handleRemove(auth_id)}
                    >
                      <Close />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={
                      user
                        ? `${user.first_name} ${user.last_name}`
                        : auth_id
                    }
                    secondary={
                      user
                        ? `${user.phone_number} · ${user.org_code}`
                        : 'User no longer a prototyper'
                    }
                    primaryTypographyProps={{ noWrap: true }}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            Add a Prototyper
          </Typography>
          {prototypers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No prototypers yet. Enable the prototyper flag on a user from the
              Group Management page before assigning them to firmware.
            </Typography>
          ) : (
            <Autocomplete<PrototyperUserInfo>
              sx={{ width: '100%' }}
              value={autocompleteValue}
              onChange={(_, v) => {
                if (v) handleAdd(v);
              }}
              options={pickerOptions}
              getOptionLabel={(p) =>
                `${p.first_name} ${p.last_name} — ${p.phone_number} (${p.org_code})`
              }
              isOptionEqualToValue={(a, b) => a.auth_id === b.auth_id}
              renderInput={(params) => (
                <TextField {...params} label="Prototyper" fullWidth />
              )}
              disabled={!!busyAuthId}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={onClose}
            variant="contained"
            disabled={!!busyAuthId}
          >
            Done
          </Button>
        </Box>
      </Box>
    </BaseModal>
  );
};

export default AssignUserModal;
